import { useMemo } from 'react';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import {
	useOptionalAnalysisBatchQuery,
	useWrittenTransactionProvenanceQuery,
} from '../AnalysisWorkspace/hooks/useDealstackAiQueries';
import { toProvenanceLine } from './provenance.utils';
import type {
	TransactionProvenanceController,
	TransactionProvenanceControllerOptions,
} from './useTransactionProvenanceController.types';

/**
 * Where a cap-table transaction came from, when a DealStack AI confirm wrote it: its batch, outcome, confirming
 * admin and documents. Behind `DEALSTACK_AI_REVIEW`; with the flag off nothing is read.
 */
export const useTransactionProvenanceController = ({
	dealId,
	transactionId,
}: TransactionProvenanceControllerOptions): TransactionProvenanceController => {
	const { value: enabled } = useDealstackFeatureFlag(FEATURE_FLAG.DEALSTACK_AI_REVIEW);
	const readable = enabled && dealId !== '' && transactionId !== undefined;
	const provenance = useWrittenTransactionProvenanceQuery(
		dealId,
		readable ? transactionId : null
	).data;
	const batch = useOptionalAnalysisBatchQuery(dealId, provenance?.batchId ?? null);
	// The line waits for the batch's title; a batch that cannot be read shows as untitled.
	const batchSettled = !batch.isLoading;

	const line = useMemo(
		() =>
			readable && provenance && batchSettled
				? toProvenanceLine(dealId, provenance, batch.data)
				: null,
		[readable, provenance, batchSettled, dealId, batch.data]
	);

	return { line };
};
